/**
 * Wire the generated `@event-manager/api-client/client` for web:
 *   - resolves browser requests against the SPA's own origin
 *   - injects `x-session-id` (Better-Auth token from memory/localStorage)
 *   - aborts requests to authed endpoints when the user has no session
 *
 * Call `bootstrapApiClient()` once during app bootstrap (before any SDK
 * function is invoked).
 */

import { client } from "@event-manager/api-client/client/client.gen";
import { DEFAULT_BACKEND_URL } from "@/shared/config/constants";
import { readAuthTokenFromStorages } from "@/shared/lib/auth/token-storage";
import { isAuthenticatedForApiCalls } from "@/shared/lib/auth-gate";
import { AuthenticationBlockedError } from "@/shared/lib/backend-errors";

/**
 * Endpoints that are SAFE to hit without a session — health probes, the
 * Better-Auth surface itself, i18n lookups, etc. Anything not on this list
 * is gated by `isAuthenticatedForApiCalls()` and the request is aborted
 * before it ever leaves the browser when the user isn't signed in.
 *
 * Keep this list narrow. New genuinely-public endpoints should be added
 * here explicitly; never widen with a catch-all.
 */
const PUBLIC_ENDPOINT_PREFIXES: readonly string[] = [
	"/api/health/",
	"/api/auth/",
	"/api/messenger-webhooks/",
	"/api/leads/",
	"/api/landing/",
	"/api/qr/",
	"/api/onboarding/",
] as const;

function isPublicEndpoint(pathname: string): boolean {
	for (const prefix of PUBLIC_ENDPOINT_PREFIXES) {
		if (pathname.startsWith(prefix)) {
			return true;
		}
	}
	return false;
}

function resolveBaseUrl(): string {
	if (typeof window === "undefined") {
		return DEFAULT_BACKEND_URL;
	}
	// The supported topology is same-origin in every browser environment:
	// Vite proxies /api in development and Caddy proxies /api in production.
	// VITE_BACKEND_URL is therefore a build/dev-server proxy target only. Never
	// trust an accidentally inlined value here: a local `.env.local` otherwise
	// makes the production site call 127.0.0.1 on the visitor's computer.
	return window.location.origin;
}

function readAuthToken(): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	return readAuthTokenFromStorages({
		windowAvailable: true,
		localStorage: window.localStorage,
	});
}

export function bootstrapApiClient(): void {
	client.setConfig({
		baseUrl: resolveBaseUrl(),
		credentials: "include",
	});

	client.interceptors.request.use((request) => {
		// Block authenticated endpoints when we know we have no session.
		// This prevents the 401 storm on the login page (notifications,
		// vacations, preferences, etc.) from firing while the user isn't
		// signed in. TanStack Query's retry policy already treats
		// AuthenticationBlockedError as "don't retry," so these never
		// turn into a request loop.
		const url = new URL(request.url);
		if (!(isPublicEndpoint(url.pathname) || isAuthenticatedForApiCalls())) {
			throw new AuthenticationBlockedError(
				`Blocked: ${url.pathname} — no active session`,
			);
		}

		const token = readAuthToken();
		if (token) {
			request.headers.set("x-session-id", token);
		}
		return request;
	});
}
